import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Workflows = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    navigate('/contact');
  };

  return (
    <section id="workflows" className="py-24 px-6 bg-black relative">
      <div className="pexadia-watermark"></div>
      <div className="max-w-7xl mx-auto relative z-20">
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-1.5 mb-6 border border-[#d4af37]/30 rounded-full bg-[#d4af37]/5">
            <span className="text-[10px] font-black tracking-[0.2em] gold-gradient uppercase">
              {t.workflows.badge}
            </span>
          </div>
          <h2 className="text-5xl md:text-7xl font-serif mb-6 italic">
            {t.workflows.title1} <span className="gold-gradient">{t.workflows.title2}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-xl font-light leading-relaxed">
            {t.workflows.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Colonne gauche : 01 / INGRESS */}
          <div className="space-y-6">
            <div className="text-sm font-black uppercase tracking-[0.2em] text-gray-500 mb-6 text-center lg:text-left">
              {t.workflows.ingress}
            </div>
            
            {t.workflows.ingressItems.map((item, index) => (
              <div key={index} className="glass-card rounded-[2rem] p-6 border border-white/10 hover:border-[#d4af37]/40 transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <i className={`fas ${['fa-bolt', 'fa-shield-alt', 'fa-database'][index]} text-[#d4af37] text-xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <div className="text-xs font-mono text-[#d4af37] mb-2">{item.metric}</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed font-light">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Colonne centrale : THE NEURAL CORE */}
          <div className="space-y-6">
            <div className="text-sm font-black uppercase tracking-[0.2em] text-gray-500 mb-6 text-center">
              {t.workflows.core}
            </div>
            
            <div className="glass-card rounded-[3rem] p-8 border-2 border-[#d4af37]/30 hover:border-[#d4af37]/50 transition-all relative overflow-hidden">
              <div className="absolute top-4 right-4 w-16 h-16 rounded-full gold-bg flex items-center justify-center opacity-20">
                <i className="fas fa-brain text-black text-2xl"></i>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-16 h-16 rounded-full gold-bg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-brain text-black text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{t.workflows.coreItem.title}</h3>
                    <div className="text-xs text-gray-500 uppercase tracking-widest">{t.workflows.coreItem.subtitle}</div>
                  </div>
                </div>
                
                <p className="text-gray-300 leading-relaxed font-light mb-6 text-sm">
                  {t.workflows.coreItem.desc}
                </p>

                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">{t.workflows.coreItem.speed}</div>
                    <div className="text-2xl font-bold gold-gradient">{t.workflows.coreItem.speedValue}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">{t.workflows.coreItem.confidence}</div>
                    <div className="text-2xl font-bold gold-gradient">{t.workflows.coreItem.confidenceValue}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : 03 / EGRESS */}
          <div className="space-y-6">
            <div className="text-sm font-black uppercase tracking-[0.2em] text-gray-500 mb-6 text-center lg:text-right">
              {t.workflows.egress}
            </div>
            
            {t.workflows.egressItems.map((item, index) => (
              <div key={index} className="glass-card rounded-[2rem] p-6 border border-white/10 hover:border-[#d4af37]/40 transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <i className={`fas ${['fa-paper-plane', 'fa-desktop', 'fa-server'][index]} text-[#d4af37] text-xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <div className="text-xs font-mono text-[#d4af37] mb-2">{item.metric}</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed font-light">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-[3rem] p-8 md:p-12 relative overflow-hidden border-white/5 shadow-2xl">
          <div className="relative z-10">
            <h3 className="text-2xl font-bold mb-6">{t.workflows.footer.title1}</h3>
            <h3 className="text-2xl font-bold mb-6 gold-gradient">{t.workflows.footer.title2}</h3>
            <p className="text-gray-400 mb-8 leading-relaxed font-light text-lg">
              {t.workflows.footer.description}
            </p>
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-8 py-5 gold-bg text-black font-bold rounded-full hover:scale-105 transition-transform uppercase tracking-widest text-xs cursor-pointer"
            >
              {t.workflows.footer.cta}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Workflows;
